import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash/fp';
import { Icon } from 'ui';
import { humanizeContentType } from '../utils';
import { TYPES } from '../../../constants/config';
import * as actions from '../../../actions/pathBuilder/contents';
import * as selectors from '../../../selectors/pathBuilder';
import Picker from '../Picker';
import Options from './Options';
import ContentWrapper from './ContentWrapper';

class Placeholder extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      replace: false,
      replaceWith: null,
      description: this.props.description || '',
    };
    this.setDescriptionRef = this.setDescriptionRef.bind(this);
    this.saveDescription = this.saveDescription.bind(this);
    this.cancelDescriptionChange = this.cancelDescriptionChange.bind(this);
    this.getOptions = this.getOptions.bind(this);
    this.handleDescriptionChange = this.handleDescriptionChange.bind(this);
    this.handleReplaceButton = this.handleReplaceButton.bind(this);
    this.onContentPickerCancel = this.onContentPickerCancel.bind(this);
  }

  getOptions() {
    return [
      {
        label: 'Delete',
        icon: 'delete',
        onClick: this.props.remove,
      },
      {
        label: 'Edit',
        icon: 'edit',
        onClick: this.props.toggleEditing,
      },
    ];
  }

  /**
   * Edit the Placeholder's Description
   */

  setDescriptionRef(node) {
    if (node) {
      this.descriptionInput = node;
      node.focus();
      global.autosize(node);
    }
  }

  handleDescriptionChange() {
    this.setState({ description: this.descriptionInput.value });
  }

  saveDescription() {
    this.props.update({ description: this.state.description });
    this.props.toggleEditing();

    if (this.props.justAdded) {
      this.props.persist();
    }
  }

  cancelDescriptionChange() {
    this.setState(
      {
        description: this.props.description,
      },
      this.props.cancelEditing,
    );
  }

  /**
   * Replacing Content via Content Picker
   */

  handleReplaceButton({ target }) {
    this.setState({ replace: true, replaceWith: target.value });
  }

  onContentPickerCancel() {
    this.setState({ replace: false, replaceWith: null });
  }

  render() {
    const {
      id,
      type,
      position,
      description,
      viewState,
      editingMode,
      stepNumber,
      isCertification,
      ...props
    } = this.props;
    const { replaceWith, replace } = this.state;

    const isReplacingContent = replace && replaceWith;
    const isEditingPlaceholder = viewState === 'editing' && !replace;
    const isNormalViewState = viewState === 'normal' && !this.state.replace;

    return (
      <ContentWrapper {...{ id, type, position, stepNumber }}>
        {isReplacingContent && (
          <Picker
            type={replaceWith}
            title={`Select a ${humanizeContentType(replaceWith)}`}
            onChange={props.replacePlaceholder}
            onCancel={this.onContentPickerCancel}
          />
        )}

        {isEditingPlaceholder && (
          <div className="PathContent PathPlaceholder">
            <Placeholder.Header
              editingMode={editingMode}
              options={
                <div className="PathContentOptions">
                  <button
                    type="button"
                    className="u-linkBtn u-linkBtn--dark td-u"
                    onClick={this.cancelDescriptionChange}
                    children="Cancel"
                  />
                  <button
                    type="button"
                    className="NewButton NewButton--blue NewButton--wide ml-16"
                    children="Done"
                    onClick={this.saveDescription}
                    disabled={this.state.description === description}
                  />
                </div>
              }
            />
            <div className="PathContent-body">
              <textarea
                className="Textarea w-fill PathPlaceholder-descriptionInput"
                rows="4"
                name="description"
                onChange={this.handleDescriptionChange}
                value={this.state.description}
                placeholder="Optionally, describe the content that will eventually replace this Placeholder."
                ref={this.setDescriptionRef}
              />
            </div>
          </div>
        )}

        {isNormalViewState && (
          <div className="PathContent PathPlaceholder">
            <Placeholder.Header
              editingMode={editingMode}
              options={<Options options={this.getOptions()} />}
            />
            {!!description && (
              <div className="PathContent-body">
                <div className="PathPlaceholder-description">{description}</div>
              </div>
            )}
            {editingMode && (
              <div className="PathPlaceholder-replaceButtons">
                <button
                  type="button"
                  value={TYPES.LESSON}
                  className="PathContentButton u-resetBtn"
                  onClick={this.handleReplaceButton}
                >
                  <Icon name="lesson" /> Replace With Lesson
                </button>
                {!isCertification && (
                  <button
                    type="button"
                    value={TYPES.PATH}
                    className="PathContentButton PathContentButton--lesson u-resetBtn"
                    onClick={this.handleReplaceButton}
                  >
                    <Icon name="path" /> Replace With Path
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </ContentWrapper>
    );
  }
}

Placeholder.Header = ({
  options, // React.ReactNode
  editingMode,
}) => (
  <div className="PathContent-header">
    <div className="PathContent-title">
      <Icon name="placeholder" className="PathContent-icon" /> Placeholder
    </div>
    {editingMode && options}
  </div>
);

const mapStateToProps = () => {
  const isEditing = selectors.isEditingContent();
  const isJustAdded = selectors.isJustAdded();
  const getStepNumber = selectors.getStepNumber();
  return (state, props) => ({
    editingMode: selectors.modeSelectors.isEdit(state.pathBuilder),
    viewState: isEditing(state.pathBuilder, props) ? 'editing' : 'normal', // TODO refactor
    justAdded: isJustAdded(state.pathBuilder, props),
    stepNumber: getStepNumber(state.pathBuilder, props),
    isCertification: !!get('certification', state.pathBuilder),
  });
};

const mapDispatchToProps = (dispatch, { id, type, position, justAdded }) => ({
  update: change => dispatch(actions.update(id, type, change)),
  persist: () => dispatch(actions.persist(id, type)),
  remove: () => dispatch(actions.remove(id, type)),
  toggleEditing: () => dispatch(actions.toggleEditing(id, type)),
  cancelEditing: () => dispatch(actions.cancelEditing(id, type, justAdded)),
  replacePlaceholder: content =>
    dispatch(actions.replacePlaceholder(id, type, content)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Placeholder);
